<template>
  <div class="boss-main-wrap boss-main-content-wrap">
    <setting-header
      v-if="isCompanyStaff"
      :breadcrumbs="[{ name: '求人確認' }, { name: '掲載前の求人', pathName: 'previewJobEditTop' }]"
      title="掲載前の求人プレビュー"
      class="hide-for-print"
    />
    <div
      data-test="usage-notes-and-print-button"
      class="grid-x hide-for-print"
    >
      <div class="cell auto pega-usage-notes">
        <mini-accordion
          v-if="hasWritePermission"
          wrap-class="pega-accordion-no-line"
          head-class="pega-accordion-link-header"
          title="利用方法・注意事項"
          data-test="mini-accordion"
        >
          <ul class="pega-disc-list">
            <li>コメント・ハイライトモードONの状態で、文字を選択すると、その位置にハイライトとコメントが作成できます。</li>
            <li>企業様管理画面で作成したハイライトは青、営業・制作担当者は緑で表示されます。</li>
            <li>コメント・ハイライトモードONの状態で、ハイライト済みの文字をクリックすると、ハイライトを削除することができます。</li>
            <li>コメント・ハイライトモードをOFFにすることで、コメントを隠してレイアウトを確認することができます。</li>
            <li>ハイライト・残文字数表示・コメントは、掲載時には表示されません。</li>
            <li>お使いのブラウザによってはどのハイライトに対するコメントかを示す線が表示されない場合があります。推奨環境（Google Chrome）をご利用くださいますようお願いいたします。</li>
            <li><job-preview-provision-list-link /></li>
          </ul>
        </mini-accordion>
      </div>
      <print-button
        class="cell shrink"
        :is-simple="false"
        @print="print"
      />
    </div>
    <!-- ボタンエリア -->
    <div
      data-test="button-area"
      class="grid-x margin-bottom-1 hide-for-print"
    >
      <div class="cell">
        <!-- メッセージ位置までジャンプするボタン offsetはヘッダ分 -->
        <button
          v-if="isFinishCreated && hasWritePermission"
          v-scroll-to="{ el: '#message', offset: -35 }"
          class="boss-button-center boss-icon-message pega-preview-jump-button pega-blue-button"
          data-test="message-jump"
        >
          {{ businessesPartnerName }}担当者とのメッセージはこちら
        </button>
      </div>
      <div class="cell">
        <button
          class="boss-button-center pega-preview-jump-button"
          @click="openSpPreview()"
        >
          <img
            class="pega-icon-phone"
            src="@/assets/images/smartphone.png"
          >
          スマホ版プレビューはこちら
        </button>
      </div>
    </div>
    <preview-job-own-table
      v-if="!isCompanyStaff && isFinishCreated"
      :job-info="jobInfo"
    />
    <div
      class="pega-preview-area"
      :class="{ 'pega-show-highlight': highlightMode }"
      data-test="preview-area"
    >
      <template v-if="showLine">
        <preview-line
          v-for="(rect, i) in rects"
          :key="i"
          :rect="rect"
        />
      </template>
      <div
        v-if="isFinishCreated"
        :id="topParentHighlightId"
        class="pega-job-preview"
        data-test="job-preview-first-area"
        @select="selected"
        @click="selected($event)"
      >
        <preview-job
          :comment-highlights="commentHighlights"
          :is-show-comment="highlightMode"
          :is-show-text-count="isShowTextCount"
          :job-info="jobInfo"
          :company-interview="companyInterview"
          :company-info="companyInfo"
          :about-apply="aboutApply"
          @toggle="toggleComment"
          @deleteComment="deleteComment"
          @addComment="addEmptyComment"
          @changeComment="addPendingComment"
          @deleteHighlight="deleteHighlightData"
          @setImgHighlight="setImgHighlight"
          @deleteImgHighlight="deleteImgHighlight"
          @deleteTextHighlight="deleteTextHighlight"
          @onLoadImage="onLoadImageAction"
          @setLine="setLine"
        />
        <preview-code-setting
          :code-setting="codeSetting"
          :official-company-name="companyInfo.officialCompanyName"
          :display-company-name="companyInfo.companyName"
          :company-name-kana="companyInfo.companyNameKana"
          :is-secret-plan="companyInfo.isSecretPlan"
          :comment-highlights="commentHighlights"
          :is-show-comment="highlightMode"
          :is-not-output-google-for-jobs="companyInfo.isNotOutputGoogleForJobs"
          :is-only-full-remote="jobInfo.isOnlyFullRemote"
          @toggle="toggleComment"
          @deleteComment="deleteComment"
          @addComment="addEmptyComment"
          @changeComment="addPendingComment"
          @deleteHighlight="deleteHighlightData"
          @deleteTextHighlight="deleteTextHighlight"
          @setLine="setLine"
        />
        <preview-matching
          v-if="matchingSetting.isStopMatching !== null"
          :matching-setting="matchingSetting"
          :comment-highlights="commentHighlights"
          :is-show-comment="highlightMode"
          @toggle="toggleComment"
          @deleteComment="deleteComment"
          @addComment="addEmptyComment"
          @changeComment="addPendingComment"
          @deleteHighlight="deleteHighlightData"
          @deleteTextHighlight="deleteTextHighlight"
          @setLine="setLine"
        />
      </div>
      <div
        v-if="isFinishCreated && personalityMatchings.length"
        class="pega-job-preview"
        data-test="job-preview-second-area"
        @select="selectedPersonalMatch"
        @click="selectedPersonalMatch"
      >
        <preview-personal-matching-status
          :personality-matching="personalityMatchings"
          :comment-highlights="commentHighlights"
          :is-show-comment="highlightMode"
          @toggle="toggleComment"
          @deleteComment="deleteComment"
          @addComment="addEmptyComment"
          @changeComment="addPendingComment"
          @deleteHighlight="deleteHighlightData"
          @setLine="setLine"
        />
      </div>
      <!-- メッセージ -->
      <job-edit-preview-message
        v-if="isFinishCreated && hasWritePermission"
        id="message"
        class="margin-bottom-3"
      />
      <!-- コメントボタン -->
      <div
        v-if="hasWritePermission"
        class="pega-commit-comment hide-for-print"
        data-test="commit-comment"
      >
        <div>
          <span style="font-size: 0.9rem; color: white; vertical-align: super;">コメント/ハイライトモード</span>
          <span class="switch tiny">
            <input
              id="tinySwitch"
              v-model="highlightMode"
              class="switch-input"
              type="checkbox"
            >
            <label
              class="switch-paddle"
              for="tinySwitch"
            >
              <span
                class="switch-active"
                aria-hidden="true"
              >ON</span>
              <span
                class="switch-inactive"
                aria-hidden="true"
              >OFF</span>
            </label>
          </span>
        </div>
        <div>
          <button
            :disabled="isDisabled"
            class="boss-button pega-button"
            data-test="comment-button"
            @click="updateComment"
          >
            <span class="boss-button-confirm-text">
              コメントを確定する
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import SettingHeader from '@/components/atoms/SettingHeader'
import PreviewJobOwnTable from '@/components/organisms/PreviewJobOwnTable'
import PreviewJob from '@/components/organisms/PreviewJob'
import PreviewCodeSetting from '@/components/organisms/PreviewCodeSetting'
import PreviewMatching from '@/components/organisms/PreviewMatching'
import PreviewPersonalMatchingStatus from '@/components/organisms/PreviewPersonalMatchingStatus'
import JobEditPreviewMessage from '@/components/molecules/JobEditPreviewMessage'
import PreviewLine from '@/components/atoms/PreviewLine'
import MiniAccordion from '@/components/atoms/MiniAccordion'
import PrintButton from '@/components/atoms/PrintButton'
import JobPreviewProvisionListLink from '@/components/atoms/JobPreviewProvisionListLink'
import { mapActions } from 'vuex'

export default {
  components: {
    SettingHeader,
    MiniAccordion,
    PrintButton,
    JobPreviewProvisionListLink,
    PreviewJobOwnTable,
    PreviewJob,
    PreviewLine,
    PreviewCodeSetting,
    PreviewMatching,
    PreviewPersonalMatchingStatus,
    JobEditPreviewMessage
  },
  async beforeRouteLeave (to, from, next) {
    let leave = true
    if (this.updateHighlights.length) {
      await this.$dialog.confirm('編集中のコメントがあります。他のページに移動してよろしいですか？').catch(() => {
        leave = false
      })
    }
    leave ? next() : next(false)
  },
  data () {
    return {
      jobId: '',
      isCompanyStaff: true,
      hasWritePermission: true,
      apiPath: '',
      isFinishCreated: false,
      highlightMode: true,
      commentHighlights: [],
      rects: [],
      updateHighlights: [],
      jobInfo: {},
      companyInterview: {},
      companyInfo: {},
      aboutApply: {},
      codeSetting: {},
      matchingSetting: {},
      personalityMatchings: [],
      callOnLoadImageCount: 0,
      isHighlighting: false
    }
  },
  computed: {
    topParentHighlightId: () => 'preview',
    noticeMessageClasses: () => ['notice', 'complement'],
    personalityHighlightIdPrefix: () => 'personality-question-',
    access () {
      return this.isCompanyStaff ? 'boss' : 'adm'
    },
    businessesPartnerName () {
      return this.isCompanyStaff ? '制作' : '企業'
    },
    isDisabled () {
      return !this.updateHighlights.length
    },
    imageCount () {
      let imageCount = 0
      if (this.jobInfo.mainPhotoId) imageCount++
      if (this.companyInterview.topicsPhotoId1) imageCount++
      if (this.companyInterview.topicsPhotoId2) imageCount++
      if (this.companyInterview.sugaoPhoto1) imageCount++
      if (this.companyInterview.sugaoPhoto2) imageCount++
      if (this.companyInterview.sugaoPhoto3) imageCount++
      return imageCount
    },
    showLine () {
      // IEからのアクセスの場合は線を表示しない
      const userAgent = window.navigator.userAgent.toLowerCase()
      const isIEAccess = (userAgent.indexOf('msie') !== -1 || userAgent.indexOf('trident') !== -1)
      return !isIEAccess && this.highlightMode
    },
    afterPrint () { return () => { this.changePrintStatus(false) } },
    isShowTextCount: () => true // 掲載前の場合は固定表示なのでtrue固定でよい
  },
  watch: {
    highlightMode () {
      this.highlightMode && this.setLine()
    }
  },
  async created () {
    this.jobId = this.$route.params.jobId
    this.isCompanyStaff = this.$route.name === 'previewJobEdit'

    // APIをADMとBOSSで分ける
    this.apiPath = this.isCompanyStaff
      ? `/preview/edit/jobs/${this.jobId}/`
      : `/preview/edit/companies/${this.$route.params.companyId}/jobs/${this.jobId}/`

    await this.setShowCommentsAndMessageStatus()
    this.callApis()
  },
  mounted () {
    // 印刷画面close時に画面上の表示を切り替える
    window.addEventListener('afterprint', this.afterPrint)
  },
  beforeDestroy () {
    // eventListenerの削除
    window.removeEventListener('afterprint', this.afterPrint)
  },
  methods: {
    ...mapActions([
      'storeMaster'
    ]),

    // create:書き込み権限によるコメントハイライト・メッセージ表示切り替え
    async setShowCommentsAndMessageStatus () {
      // コメント情報の取得・表示切り替え
      if (!this.isCompanyStaff) {
        // 書き込み権限確認API
        await this.$http.secured.get(this.apiPath + 'has_write_permission').then(response => {
          this.$set(this, 'hasWritePermission', response.data)
          this.highlightMode = this.hasWritePermission
        })
      }
    },
    // create:API呼び出し
    callApis () {
      const apis = [
        // 求人情報取得API
        this.$http.secured.get(this.apiPath + 'job_info'),
        // もっと知りたい！会社の素顔API
        this.$http.secured.get(this.apiPath + 'company_interview'),
        // 企業情報取得API
        this.$http.secured.get(this.apiPath + 'company_info'),
        // 応募について取得API
        this.$http.secured.get(this.apiPath + 'about_apply'),
        // コード設定取得API
        this.$http.secured.get(this.apiPath + 'code_settings'),
        // マッチング条件設定取得API
        this.$http.secured.get(this.apiPath + 'matching_settings'),
        // パーソナリティ設定取得API
        this.$http.secured.get(this.apiPath + 'personality_matching')
      ]

      if (this.hasWritePermission) {
        // コメントハイライト一覧取得API
        apis.push(this.$http.secured.get(this.apiPath + 'comments'))
      }

      // コード設定・マッチング設定で利用するマスタを事前に取得しておく
      // 子コンポーネントで取得しようとすると描画順がAPIのレスポンスタイミングによって担保できないため。
      apis.push(
        this.storeMaster('businesses'),
        this.storeMaster('occupations'),
        this.storeMaster('skillExp'),
        this.storeMaster('skills'),
        this.storeMaster('licenses'),
        this.storeMaster('prIconDisplay')
      )

      Promise.all(apis).then(([
        jobInfoResponse,
        companyInterviewResponse,
        companyInfoResponse,
        aboutApplyResponse,
        codeSettingResponse,
        matchingSettingResponse,
        personalityMatchingsResponse,
        commentsResponse]) => {
        this.$set(this, 'jobInfo', jobInfoResponse.data)
        this.$set(this, 'companyInterview', companyInterviewResponse.data)
        this.$set(this, 'companyInfo', companyInfoResponse.data)
        this.$set(this, 'aboutApply', aboutApplyResponse.data)
        this.$set(this, 'codeSetting', codeSettingResponse.data)
        this.$set(this, 'matchingSetting', matchingSettingResponse.data)
        this.$set(this, 'personalityMatchings', personalityMatchingsResponse.data)
        if (this.hasWritePermission) {
          this.$set(this, 'commentHighlights', commentsResponse.data)
        }
        this.isFinishCreated = true
        if (!this.imageCount) {
          this.onLoadImageAction() // 画像の読み込みがないため、画像読み込み後処理を呼んでおく
        }
      })
    },

    // 画像読み込み後処理
    onLoadImageAction () {
      this.callOnLoadImageCount++
      if (this.imageCount && this.callOnLoadImageCount !== this.imageCount) return// まだ全ての画像がロードされていないため処理を省略

      // 座標の記録
      this.setOffsetPositionForCommentHighlights()
      this.sortHighlightData()
    },

    // ハイライト一覧全てにハイライト位置座標を記録
    setOffsetPositionForCommentHighlights () {
      _.forEach(this.commentHighlights, async (commentHighlight) => {
        if (!commentHighlight.highlight) return // ハイライト情報がない場合は描画不要
        const highlightElement = document.getElementById(this.createHighlightId(commentHighlight.target, commentHighlight.highlight.start))
        if (!highlightElement) return // 画面上からハイライト情報が取得できない場合は描画不要

        // ハイライト座標をDataに記録
        this.setOffsetPosition(commentHighlight, highlightElement)
      })
    },

    // ハイライト位置座標を記録
    setOffsetPosition (positionObject, highlightElement) {
      this.$set(positionObject, 'top', highlightElement.offsetTop)
      this.$set(positionObject, 'left', highlightElement.offsetLeft)

      let parent = highlightElement.parentElement
      // 多重ハイライトを考慮
      while (parent.tagName === 'MARK' || parent.id === '') { parent = parent.parentElement }
      if (parent.tagName === 'TD') {
        // td内のハイライトの場合のみpage上の座標を取れないため、親位置を算出
        this.$set(positionObject, 'parentTop', parent.offsetTop)
      } else {
        // それ以外は親位置を利用したくないため０とする
        this.$set(positionObject, 'parentTop', 0)
      }
    },

    // 選択時処理
    async selected (e = null) {
      // ハイライトモードではない場合or別のハイライト処理が実行中の場合は動作しない
      if (!this.highlightMode || this.isHighlighting) return

      // 連打制御フラグ
      this.isHighlighting = true

      await this.selectedAction(e)

      // 連打制御フラグを戻す
      this.isHighlighting = false
    },

    async selectedAction (e = null) {
      // 画像ハイライト判定
      if (_.get(e, 'target.tagName') === 'IMG') {
        if (e.target.id) await this.createImgHighlight(e.target) // idが取れる場合のみ処理実行
        return
      }

      // 選択位置の取得
      const selection = window.getSelection()

      if (!selection.anchorNode.nodeName || selection.anchorNode.nodeName !== '#text') {
        // text,画像以外を選択されている場合は処理なし
        return
      }

      const selectItem = {}
      selectItem.text = selection.toString()
      // 対象エリアを親IDから取得
      selectItem.target = this.getId(selection.focusNode)
      // 親IDでエリアが特定できない場合には処理なしで終了させる
      if (selectItem.target === this.topParentHighlightId) return
      // ハイライト不可クラスの場合には処理なしで終了する
      if (this.noticeMessageClasses.some(noticeMessageClass => selection.anchorNode.parentNode.className === noticeMessageClass)) return
      if (this.noticeMessageClasses.some(noticeMessageClass => selection.focusNode.parentNode.className === noticeMessageClass)) return

      // ハイライト済みの箇所を選択された場合は削除処理を実施
      if (this.isHighlighted(selection)) {
        if (!selectItem.text.length) {
          this.deleteHighlightElement(selection, selectItem)
          return
        } else {
          this.$toasted.global.error({ message: '既にハイライトされているため、ハイライトできませんでした。' })
          // 範囲選択の削除
          window.getSelection().empty()
          return
        }
      }

      // ハイライト箇所以外の0文字なら処理なし
      if (!selectItem.text.length) return

      // ハイライト情報の保管
      await this.addHighlight(selection, selectItem)

      // 範囲選択の削除
      window.getSelection().empty()
    },

    // ハイライト済み判定
    isHighlighted (selection) {
      // 開始ノードまたは終了ノードの親idがhighlight-***であること
      return this.getId(selection.anchorNode).indexOf('highlight') > -1 || this.getId(selection.focusNode).indexOf('highlight') > -1
    },

    // ハイライト付与
    async addHighlight (selection, selectItem) {
      if (this.getId(selection.anchorNode) !== this.getId(selection.focusNode)) {
        // Rangeが複数のエリアを跨いでいる場合は処理を実施せず返却
        this.$toasted.global.error({ message: 'エリアを跨いでいる、または項目名を含んでいるため、ハイライトできませんでした。' })
        return
      }
      // ハイライトの付与（DOM書き換え）
      const newHighlight = document.createElement('mark')
      newHighlight.className = 'pega-highlight-' + this.access
      const selectionRange = selection.getRangeAt(0)
      newHighlight.appendChild(selectionRange.extractContents())
      selectionRange.insertNode(newHighlight)

      // 開始位置と終了位置の取得
      const afterRange = document.createRange()
      let parentNode = selection.focusNode
      while (!parentNode.id) { parentNode = parentNode.parentNode }
      afterRange.setStart(parentNode, 0)
      afterRange.setEnd(selection.anchorNode, selection.anchorOffset)
      selectItem.index = {}
      selectItem.index.start = afterRange.toString().length
      selectItem.index.end = selectItem.index.start + selectItem.text.length

      await this.createHighlight(selectItem, newHighlight)
    },

    // パーソナルマッチング選択時処理
    async selectedPersonalMatch () {
      if (!this.highlightMode) return // ハイライトモードではない場合は動作しない
      // 選択位置の取得
      const selectItem = {}
      const selection = window.getSelection()
      selectItem.target = this.getId(selection.focusNode)
      // 親IDが企業パーソナリティ設定以外の場合は処理なしとする
      if (selectItem.target.indexOf(this.personalityHighlightIdPrefix) < 0) {
        return
      }

      // ハイライト済みの箇所を選択された場合は削除処理を実施
      const commentHighlight = this.getCommentHighlightOnlyOne(selectItem.target)
      if (commentHighlight) {
        this.deleteHighlightComment(commentHighlight)
        return // 以降の処理は実施しない
      }

      // ハイライトの登録
      const questionIdNum = Number(selectItem.target.replace(this.personalityHighlightIdPrefix, ''))

      const selectItemData = {
        target: selectItem.target,
        text: '会社・職場のパーソナリティ設定' + questionIdNum,
        index: {
          start: questionIdNum,
          end: questionIdNum + 1
        }
      }
      const rectData = {
        // DBには登録しない値
        parentTop: selection.anchorNode.parentNode.offsetTop,
        // parentTopで一意に決まるが一応値をセットする（tableのためoffsetTopが取得できない）
        top: questionIdNum,
        left: questionIdNum
      }

      this.createHighlight(selectItemData, null, rectData)

      // 範囲選択の削除
      window.getSelection().empty()
    },

    // HTML IDの取得
    getId (node) {
      let parent = node.parentNode
      while (!parent.id) {
        parent = parent.parentNode
      }
      return parent.id
    },

    // 画像ハイライト作成
    async createImgHighlight (element) {
      const commentHighlight = this.getCommentHighlightOnlyOne(element.id)
      if (commentHighlight) {
        this.deleteHighlightComment(commentHighlight).then(isDelete => {
          if (isDelete) {
            this.deleteImgHighlight(element)
          }
        })
        return
      }
      const newHighlight = this.imgHighlight(element)
      if (!newHighlight) return
      const selectItem = {
        target: element.id,
        text: '写真(' + element.alt + ')',
        index: {
          start: 0,
          end: 1
        }
      }
      await this.createHighlight(selectItem, newHighlight)
    },

    // 画像ハイライト設定(初期描画時処理)
    setImgHighlight (element, commentHighlight) {
      const newHighlight = this.imgHighlight(element, (commentHighlight.isCompanyStaff ? 'boss' : 'adm'))
      if (!newHighlight) return
      newHighlight.id = this.createHighlightId(commentHighlight.target, commentHighlight.highlight.start)
    },

    // 画像ハイライト実施
    imgHighlight (element, highlightClass = null) {
      const targetRange = document.createRange()
      targetRange.selectNode(element)
      const newHighlight = document.createElement('span')
      newHighlight.className = 'pega-highlight-' + (highlightClass || this.access)
      try {
        targetRange.surroundContents(newHighlight)
      } catch (e) {
        // 利用想定としては発生しない想定だが、surroundContentsがエラーになった場合の処理
        this.$toasted.global.error({ message: 'ハイライトできませんでした。' })
        return null
      }
      return newHighlight
    },

    // 画像ハイライト削除
    deleteImgHighlight (element) {
      const targetNode = element.parentNode
      targetNode.parentNode.insertBefore(element, targetNode)
      targetNode.remove()
    },

    // コメントハイライト取得
    getCommentHighlightOnlyOne (target) {
      // ターゲット名：コメント ＝ １：１ のみ利用可能(画像、テーブル行ハイライト)
      return this.commentHighlights.find(i => i.highlight && target.indexOf(i.target) > -1)
    },

    // ハイライト削除（引数：コメントオブジェクト）
    async deleteHighlightComment (commentHighlight) {
      if (this.isCompanyStaff && !commentHighlight.isCompanyStaff) {
        // 企業担当者は、企業担当者以外が作成したハイライトを削除できない
        this.$toasted.global.error({ message: '相手側の作成したハイライトであるため、削除できません。' })
        return false
      }
      let isDelete = false
      if (commentHighlight.comments.every(i => i.comment === '')) {
        // 双方コメントがない場合はコメントごと削除
        this.deleteComment(commentHighlight) // setLineつき
        this.$toasted.global.success({ message: 'ハイライトとコメントを削除しました。' })
        isDelete = true
      } else {
        await this.$dialog.confirm('このハイライトを削除しますか？')
          // はいを押下した時
          .then(() => {
            this.deleteHighlightData(commentHighlight)
            this.$toasted.global.success({ message: 'ハイライトを削除しました。' })
            this.setLine()
            isDelete = true
          })
          // いいえを押下した時
          .catch(() => { isDelete = false })
      }
      return isDelete
    },

    // ハイライト削除（引数：エレメント）
    deleteHighlightElement (selection, selectItem) {
      // コメントを取得
      const parentNode = selection.anchorNode.parentNode
      const commentId = selectItem.target.replace('highlight-', '')
      const commentHighlight = this.commentHighlights.find(i => i.highlight && i.target + '-' + i.highlight.start === commentId)

      this.deleteHighlightComment(commentHighlight).then(isDelete => {
        if (isDelete) {
          this.deleteTextHighlight(parentNode)
        }
      })
    },

    // 画面上のテキストハイライトを削除
    deleteTextHighlight (node) {
      let targetNode = node
      while (targetNode.tagName !== 'MARK') { targetNode = targetNode.parentNode }

      while (targetNode.firstChild) {
        targetNode.parentNode.insertBefore(targetNode.firstChild, targetNode)
      }
      targetNode.remove()
    },

    // ハイライト作成（共通部）highlightElementかrectいずれかは必須の前提
    async createHighlight (selectItem, highlightElement, rect = null) {
      // ハイライト必要データ準備
      const request = {
        target: selectItem.target,
        text: selectItem.text.length > 50 ? selectItem.text.substr(0, 50) + '...' : selectItem.text,
        isOpen: true,
        isCompanyStaff: this.isCompanyStaff,
        comments: [],
        highlight: {
          start: selectItem.index.start,
          end: selectItem.index.end
        }
      }
      // rectの指定がなければrectを生成
      let rectData = rect
      if (!rectData) {
        rectData = {}
        this.setOffsetPosition(rectData, highlightElement)
      }
      // ハイライト登録API
      await this.$http.secured.post(this.apiPath + 'comments', request).then(response => {
        // ハイライトデータを登録
        this.commentHighlights.push({ ...response.data, ...rectData })
      })

      // ハイライトエレメントがあればIDを指定
      highlightElement && (highlightElement.id = this.createHighlightId(selectItem.target, selectItem.index.start))

      this.sortHighlightData()

      this.$toasted.global.success({ message: 'ハイライトを作成しました。' })
    },

    // コメント削除（判定つき）
    deleteComment (value) {
      // コメントの削除
      const commentIndex = this.commentHighlights.findIndex(i => i.id === value.id)
      if (commentIndex !== -1) {
        // コメント削除
        this.callDeleteComment(commentIndex)
        this.setLine()
      }
    },

    // コメント削除API
    callDeleteComment (index) {
      const id = this.commentHighlights[index].id
      // コメント削除API
      this.$http.secured.delete(this.apiPath + `comments/${id}`)
      // コメントデータ削除
      this.commentHighlights.splice(index, 1)
      // 更新リストに未確定コメントがあった場合も削除
      const pendingCommentIndex = this.updateHighlights.findIndex(i => i.parentId === id)
      if (pendingCommentIndex !== -1) {
        this.updateHighlights.splice(pendingCommentIndex, 1)
      }
    },

    // 空コメント追加
    addEmptyComment (value) {
      value.comments.push({
        comment: '',
        isCompanyStaff: this.isCompanyStaff
      })
    },

    // 未確定コメントの追加
    addPendingComment (value) {
      // 既に未確定コメントに存在する場合は処理をスキップ
      if (this.updateHighlights.some(updateHighlight => updateHighlight.parentId === value.id)) return

      const target = value.comments.filter(comment => comment.isCompanyStaff === this.isCompanyStaff)[0] // 対象は必ず1件取得できるロジックとなっている
      // コメント詳細の状態を「未確定」にする
      this.$set(target, 'isPending', true)
      // コメント詳細を更新リストに追加
      target.parentId = value.id
      target.parentTitle = value.text
      this.updateHighlights.push(target)
    },

    // ハイライト削除API呼び出し・ハイライト削除
    deleteHighlightData (value) {
      // ハイライト削除API
      this.$http.secured.delete(this.apiPath + `comments/${value.id}/_highlight`)
      // ハイライトデータ削除
      this.$delete(value, 'highlight')
    },

    // コメント表示の開閉状態を記録
    async toggleComment (value) {
      value.isOpen = !value.isOpen
      this.$http.secured.put(this.apiPath + `comments/${value.id}`, value)
      this.setLine()
    },

    // コメント更新
    updateComment () {
      // コメント詳細一括登録・更新API
      this.$http.secured.put(this.apiPath + 'comments', this.updateHighlights).then(response => {
        this.$set(this, 'commentHighlights', response.data)
        this.setOffsetPositionForCommentHighlights()
        this.$set(this, 'updateHighlights', [])
        this.$toasted.global.success({ message: 'コメントを更新しました。' })
        this.sortHighlightData()
      })
    },

    // コメントハイライト情報をハイライト位置でソート
    async sortHighlightData () {
      await this.$nextTick()
      // ハイライトのソート
      this.commentHighlights.sort((a, b) => {
        // ハイライトがないものは末尾
        if (!a.highlight) return 1
        if (!b.highlight) return -1
        // ハイライト位置でソート
        return a.parentTop - b.parentTop ||
          a.top - b.top ||
          a.left - b.left ||
          a.highlight.start - b.highlight.start ||
          0
      })
      // コメント線の描画
      this.setLine()
    },

    // ハイライトーコメント線描画
    async setLine () {
      this.$set(this, 'rects', [])
      await this.$nextTick() // PreviewJobの描画分の待機
      _.forEach(this.commentHighlights, async (commentHighlight) => {
        if (!commentHighlight.highlight) return // ハイライト情報がない場合は描画不要
        await this.$nextTick()
        const highlight = document.getElementById(this.createHighlightId(commentHighlight.target, commentHighlight.highlight.start))
        const comment = document.getElementById('comment-' + commentHighlight.target + '-' + commentHighlight.highlight.start)
        if (!highlight || !comment) return // ハイライト・コメントが取得できない場合は描画しない

        const highlightRect = highlight.getBoundingClientRect()
        const commentRect = comment.getBoundingClientRect()
        this.rects.push({
          id: commentHighlight.id,
          x1: highlightRect.right,
          y1: highlightRect.top,
          x2: commentRect.left,
          y2: commentRect.top + 10 // マージン
        })
      })
    },

    // ハイライト用 HTML ID 作成
    createHighlightId (target, index) {
      return 'highlight-' + target + '-' + index
    },

    // スマホプレビュー表示
    openSpPreview () {
      const routeData = this.isCompanyStaff
        ? this.$router.resolve({ name: 'previewJobSp', params: { jobId: this.jobId } })
        : this.$router.resolve({ name: 'admPreviewJobSp', params: { companyId: this.$route.params.companyId, jobId: this.jobId } })
      window.open(routeData.href, `window_sp_${this.jobId}`, 'width=414,height=736,scrollbars=1')
    },

    // 印刷
    async print () {
      await this.changePrintStatus(true)
      window.print()
    },

    // 印刷ステータス切り替え
    async changePrintStatus (status) {
      // 印刷用にハイライト・コメント表示を切り替える（印刷時非表示）
      this.$set(this, 'highlightMode', !status)
      await this.$nextTick()
    }
  }
}
</script>
